import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { styled } from "@mui/system";
import SettingsIcon from "@mui/icons-material/Settings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import MiturLogo from "../../../assets/images/MiturLogoSecondary.png";
import AddIcon from "@mui/icons-material/Add";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EmailIcon from "@mui/icons-material/Email";
import FolderIcon from "@mui/icons-material/Folder";
import HistoryIcon from "@mui/icons-material/History";
import AppsIcon from "@mui/icons-material/Apps";
import BarChartIcon from "@mui/icons-material/BarChart";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import CampaignIcon from "@mui/icons-material/Campaign";
import HelpIcon from "@mui/icons-material/Help";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Dialog, DialogTitle, DialogContent, Button as MuiButton } from "@mui/material";
import { useQuery } from "react-query";
import { getAllServices } from "../../../api/ListOfServicesPerCategory";
import { getAllRepresentativeAndApplicant } from "../../../api/myCompany";
import { generateSlug } from "../../../utils/slugify";

import { removeLocalStorageSessionData } from "../../../auth/AuthFunctions";
import { AuthLogout } from "../../../redux/actions/AuthActions";
import { ShowGlobalLoading } from "../../../redux/actions/UiActions";
import { CleanUrl } from "../../../redux/actions/UrlActions";
import FONTS_FAMILY from "../../../theme/FontsFamily";
import LocalStorageService from "../../../services/LocalStorageService";

const SidebarContainer = styled("nav")(({ collapsed }) => ({
    width: collapsed ? "72px" : "240px",
    backgroundColor: "var(--sidebar-bg, #F8F9FD)",
    borderRight: "1px solid var(--sidebar-border, #E2E8F0)",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "sticky",
    top: 0,
    boxSizing: "border-box",
    padding: collapsed ? "12px 8px 20px 8px" : "12px 16px 20px 16px",
    gap: "16px",
    flexShrink: 0,
    zIndex: 40,
    transition: "width 0.2s ease, padding 0.2s ease",
    overflowY: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        display: "none",
        width: "0px",
        height: "0px",
        background: "transparent",
    },
}));

const LogoSection = styled("div")({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "0 16px",
    gap: "8px",
    marginBottom: "4px",
});

const NewTransactionButton = styled("button")(({ collapsed }) => ({
    background: "#0063bc",
    color: "#FFFFFF",
    border: "none",
    borderRadius: collapsed ? "10px" : "8px",
    height: "40px",
    minHeight: "40px",
    width: collapsed ? "38px" : "100%",
    margin: collapsed ? "0 auto" : "0",
    padding: collapsed ? "0" : "0 14px",
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "13px",
    fontWeight: "700",
    letterSpacing: "0.02em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    cursor: "pointer",
    boxShadow: "none",
    transition: "all 0.2s ease",
    position: "relative",
    flexShrink: 0,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "none",
        background: "#0072d6",
        "& svg": {
            transform: "rotate(90deg)",
        }
    },
    "& svg": {
        fontSize: "18px",
        transition: "transform 0.3s ease",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: "none",
    },
}));

const MenuSection = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "8px",
});

const MenuItem = styled("a")(({ active, collapsed }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: collapsed ? "center" : "flex-start",
    gap: collapsed ? "0" : "12px",
    padding: collapsed ? "10px 0" : "10px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "15px",
    textDecoration: "none",
    transition: "all 0.15s ease",
    backgroundColor: active ? "var(--sidebar-active-bg, rgba(3, 13, 57, 0.06))" : "transparent",
    color: active ? "var(--sidebar-active-text, #192873)" : "var(--sidebar-text, #5C5F6A)",
    fontWeight: active ? "600" : "500",
    width: collapsed ? "44px" : "auto",
    height: collapsed ? "44px" : "auto",
    margin: collapsed ? "0 auto" : "0",
    position: "relative",
    "& span.label": {
        display: collapsed ? "none" : "inline",
        flex: 1,
    },
    "& svg": {
        color: active ? "var(--sidebar-active-text, #192873)" : "var(--sidebar-icon, #767680)",
        fontSize: "20px",
        transition: "color 0.15s ease",
    },
    "&::before": (active && !collapsed) ? {
        content: '""',
        position: "absolute",
        left: "0",
        width: "3px",
        height: "18px",
        borderRadius: "0 2px 2px 0",
        backgroundColor: "#B7102A",
    } : {},
    "&:hover": {
        backgroundColor: active ? "var(--sidebar-active-bg, rgba(3, 13, 57, 0.06))" : "var(--sidebar-hover-bg, rgba(3, 13, 57, 0.03))",
        color: "var(--sidebar-hover-text, #192873)",
        "& svg": {
            color: "var(--sidebar-hover-text, #192873)",
        }
    },
}));

const BottomSection = styled("div")({
    marginTop: "auto",
    borderTop: "1px solid #E2E8F0",
    paddingTop: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
});

const LogoutMenuItem = styled("a")(({ collapsed }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: collapsed ? "center" : "flex-start",
    gap: collapsed ? "0" : "12px",
    padding: collapsed ? "10px 0" : "10px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "15px",
    textDecoration: "none",
    transition: "all 0.15s ease",
    color: "#5C5F6A",
    width: collapsed ? "44px" : "auto",
    height: collapsed ? "44px" : "auto",
    margin: collapsed ? "0 auto" : "0",
    "& span": {
        display: collapsed ? "none" : "inline",
    },
    "& svg": {
        color: "#767680",
        fontSize: "20px",
        transition: "color 0.15s ease",
    },
    "&:hover": {
        backgroundColor: "rgba(183, 16, 42, 0.06)",
        color: "#B7102A",
        "& svg": {
            color: "#B7102A",
        }
    },
}));

const SubmenuContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    paddingLeft: "32px",
    marginTop: "2px",
    marginBottom: "6px",
    maxHeight: "220px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
        width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#CBD5E1",
        borderRadius: "4px",
    },
});

const SubmenuItem = styled("a")(({ active }) => ({
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: FONTS_FAMILY.bold.body,
    fontSize: "12px",
    lineHeight: "16px",
    textDecoration: "none",
    transition: "all 0.15s ease",
    backgroundColor: active ? "rgba(3, 13, 57, 0.06)" : "transparent",
    color: active ? "#192873" : "#5C5F6A",
    fontWeight: active ? "600" : "500",
    "&:hover": {
        backgroundColor: "rgba(3, 13, 57, 0.04)",
        color: "#192873",
    },
}));

function DashboardSidebar({ collapsed, onCloseMobile }) {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    const [openModal, setOpenModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedServiceId, setSelectedServiceId] = useState("");
    const [selectedCompany, setSelectedCompany] = useState("");
    const [companies, setCompanies] = useState([]);
    const [servicesSubmenuOpen, setServicesSubmenuOpen] = useState(false);

    const { data: listOfServices } = useQuery(["listOfServices"], () => getAllServices());
    const { data: allCompanyImIn } = useQuery(["allRepresentativeApplicantData"], () =>
        getAllRepresentativeAndApplicant()
    );

    useEffect(() => {
        if (allCompanyImIn?.payload) {
            const list = allCompanyImIn.payload.map((x) => ({
                company_name: x.company_name,
                id: x.id
            }));
            setCompanies(list);
        }
    }, [allCompanyImIn]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("openNewRequest") === "true") {
            setOpenModal(true);
            const compId = params.get("company_id");
            const compName = params.get("company_name");
            if (compId && compName) {
                setSelectedCompany(`${compId},${compName}`);
            }
        }
    }, [location.search]);

    const HandleLogOut = (e) => {
        e.preventDefault();
        dispatch(ShowGlobalLoading(isEn ? "Logging out" : "Cerrando sesión"));
        setTimeout(() => {
            removeLocalStorageSessionData();
            dispatch(AuthLogout());
            dispatch(CleanUrl());
            window.location.reload();
        }, 1500);
    };

    const getCategoryIcon = (catName) => {
        const str = String(catName || "").toLowerCase();
        if (str.includes("dpp") || str.includes("proyecto") || str.includes("evaluacion")) return <BarChartIcon />;
        if (str.includes("confotur") || str.includes("clasificacion")) return <BusinessIcon />;
        if (str.includes("licencia") || str.includes("operacion")) return <BadgeIcon />;
        if (str.includes("mercadeo") || str.includes("patrocinio")) return <CampaignIcon />;
        return <ApartmentIcon />;
    };

    const getCategoryShortName = (catName) => {
        const str = String(catName || "");
        if (str.includes("DPP") || str.includes("Tramitación y Evaluación de Proyectos")) return "DPP";
        if (str.includes("CONFOTUR") || str.includes("Clasificaciones de CONFOTUR")) return "CONFOTUR";
        if (str.includes("Licencias") || str.includes("Licencias de Operación")) return isEn ? "Licenses" : "Licencias";
        if (str.includes("Mercadeo") || str.includes("Mercadeo y Patrocinio")) return isEn ? "Marketing" : "Mercadeo";
        return str;
    };

    const mainMenuItems = [
        { id: "dashboard", label: isEn ? "Home" : "Inicio", icon: <DashboardIcon />, active: location.pathname === "/app/mi-escritorio" && !location.search.includes("tab=3"), path: "/app/mi-escritorio" },
        { id: "empresas", label: isEn ? "Companies" : "Empresas", icon: <ApartmentIcon />, active: location.pathname === "/app/empresas" || (location.pathname === "/app/mi-escritorio" && location.search.includes("tab=3")), path: "/app/empresas" },
        { id: "mensajes", label: isEn ? "Messages" : "Mensajes", icon: <EmailIcon />, active: location.pathname === "/app/mensajes", path: "/app/mensajes" },
        { id: "documentos", label: isEn ? "Documents" : "Documentos", icon: <FolderIcon />, active: location.pathname === "/app/documentos", path: "/app/documentos" },
        { id: "historial", label: isEn ? "History" : "Historial", icon: <HistoryIcon />, active: location.pathname === "/app/historial", path: "/app/historial" },
    ];

    // Carga dinámica de servicios/categorías desde la API de backend (soporta existentes y futuros)
    const searchParams = new URLSearchParams(location.search);
    const currentCategoryParam = searchParams.get("categoria");

    const dynamicCategoryItems = (listOfServices || []).map((cat) => {
        const catSlug = generateSlug(cat.name);
        const isCategoryActive =
            location.pathname === "/todos-los-tramites" &&
            currentCategoryParam &&
            (currentCategoryParam === String(cat.id) || currentCategoryParam === catSlug);

        return {
            id: cat.id,
            label: getCategoryShortName(cat.name),
            fullTitle: cat.name,
            icon: getCategoryIcon(cat.name),
            active: Boolean(isCategoryActive),
            path: `/todos-los-tramites?categoria=${cat.id}`
        };
    });

    const modalCategories = (listOfServices || []).map((cat) => ({
        id: cat.id,
        title: cat.name,
        description: cat.description || `Servicios y trámites pertenecientes a ${cat.name}.`,
        icon: getCategoryIcon(cat.name),
        services: cat.services || []
    }));

    const getServicesForCategory = (catId) => {
        if (!listOfServices) return [];
        const cat = (listOfServices || []).find(c => String(c.id) === String(catId));
        return cat ? cat.services || [] : [];
    };

    const handleContinue = () => {
        const services = getServicesForCategory(selectedCategory);
        const selectedService = services.find(s => String(s.id) === String(selectedServiceId));
        if (selectedService) {
            setOpenModal(false);
            const slug = selectedService.slug || generateSlug(selectedService.name);
            if (!selectedCompany) {
                history.push(`/solicitar-tramite/${slug}`);
            } else {
                const [company_id, company_name] = selectedCompany.split(",");
                history.push(`/solicitar-tramite/${slug}/${company_id}/${company_name}`);
            }
        }
    };

    const closeModal = () => {
        setOpenModal(false);
        setSelectedCategory(null);
        setSelectedServiceId("");
        setSelectedCompany("");
    };

    const go = (path) => (e) => {
        e.preventDefault();
        if (onCloseMobile) onCloseMobile();
        if (path.startsWith("/app")) {
            history.push(path);
        } else {
            window.location.href = path;
        }
    };

    return (
        <SidebarContainer collapsed={collapsed}>
            <LogoSection style={{ width: "100%", padding: "0", display: "flex", justifyContent: "center" }}>
                {collapsed ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#192873",
                            borderRadius: "12px",
                            width: "44px",
                            height: "44px",
                            color: "#FFFFFF",
                            cursor: "pointer"
                        }}
                        onClick={() => history.push("/")}
                    >
                        <ApartmentIcon style={{ fontSize: "24px" }} />
                    </div>
                ) : (
                    <img
                        src={MiturLogo}
                        alt="Ministerio de Turismo"
                        style={{ width: "100%", maxHeight: "55px", objectFit: "contain", cursor: "pointer" }}
                        onClick={() => history.push("/")}
                    />
                )}
            </LogoSection>

            <NewTransactionButton
                collapsed={collapsed}
                onClick={() => { setSelectedCategory(null); setOpenModal(true); }}
            >
                <AddIcon /> {!collapsed && (isEn ? "New Application" : "Nuevo Trámite")}
            </NewTransactionButton>

            <MenuSection>
                {mainMenuItems.map((item) => (
                    <MenuItem
                        key={item.id}
                        active={item.active}
                        collapsed={collapsed}
                        onClick={go(item.path)}
                        href="#"
                    >
                        {item.icon}
                        <span className="label">{item.label}</span>
                    </MenuItem>
                ))}

                {/* Opción agrupadora: Servicios con submenú desplegable */}
                <MenuItem
                    active={dynamicCategoryItems.some((cat) => cat.active) || location.pathname === "/todos-los-tramites"}
                    collapsed={collapsed}
                    onClick={(e) => {
                        e.preventDefault();
                        setServicesSubmenuOpen(!servicesSubmenuOpen);
                    }}
                    href="#"
                >
                    <AppsIcon />
                    <span className="label">{isEn ? "Services" : "Servicios"}</span>
                    {!collapsed && (servicesSubmenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </MenuItem>

                {servicesSubmenuOpen && !collapsed && (
                    <SubmenuContainer>
                        <SubmenuItem
                            href="#"
                            onClick={go("/todos-los-tramites")}
                            active={location.pathname === "/todos-los-tramites" && !currentCategoryParam}
                        >
                            {isEn ? "All Services" : "Todos los servicios"}
                        </SubmenuItem>
                        {dynamicCategoryItems.map((cat) => (
                            <SubmenuItem
                                key={`cat-${cat.id}`}
                                href="#"
                                title={cat.fullTitle}
                                onClick={go(cat.path)}
                                active={cat.active}
                            >
                                {cat.label}
                            </SubmenuItem>
                        ))}
                    </SubmenuContainer>
                )}
            </MenuSection>

            <BottomSection>
                <MenuItem
                    active={location.pathname === "/app/mi-cuenta/configuracion"}
                    collapsed={collapsed}
                    onClick={go("/app/mi-cuenta/configuracion")}
                    href="#"
                >
                    <SettingsIcon />
                    <span className="label">{isEn ? "Settings" : "Configuración"}</span>
                </MenuItem>

                <MenuItem
                    active={false}
                    collapsed={collapsed}
                    onClick={go("/preguntas-frecuentes")}
                    href="#"
                >
                    <HelpIcon />
                    <span className="label">{isEn ? "Help" : "Ayuda"}</span>
                </MenuItem>
                <LogoutMenuItem collapsed={collapsed} onClick={HandleLogOut} href="#">
                    <LogoutIcon />
                    <span>{isEn ? "Log Out" : "Cerrar Sesión"}</span>
                </LogoutMenuItem>
            </BottomSection>

            {/* Modal de Iniciar Nuevo Trámite */}
            <Dialog open={openModal} onClose={closeModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: FONTS_FAMILY.bold.title, color: "#192873", fontWeight: 700 }}>
                    Iniciar Nuevo Trámite
                </DialogTitle>
                <DialogContent>
                    <p style={{ color: "#767680", fontSize: "14px", margin: "0 0 24px 0", fontWeight: "500" }}>
                        Seleccione el tipo de servicio que desea solicitar
                    </p>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                        {modalCategories.map((cat) => {
                            const isSelected = selectedCategory === cat.id;
                            return (
                                <div
                                    key={cat.id}
                                    onClick={() => { setSelectedCategory(cat.id); setSelectedServiceId(""); setSelectedCompany(""); }}
                                    style={{
                                        border: isSelected ? "2px solid #192873" : "1px solid #E1E2EC",
                                        borderRadius: "14px",
                                        padding: "20px 14px",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        gap: "12px",
                                        cursor: "pointer",
                                        backgroundColor: isSelected ? "#F0F3FF" : "#FFFFFF",
                                        boxShadow: "none",
                                        transition: "all 0.2s ease"
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "44px",
                                            height: "44px",
                                            borderRadius: "10px",
                                            backgroundColor: isSelected ? "#192873" : "#F0F3FF",
                                            color: isSelected ? "#FFFFFF" : "#192873",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0
                                        }}
                                    >
                                        {cat.icon}
                                    </div>
                                    <span style={{ fontWeight: "700", fontSize: "13.5px", color: "#192873", lineHeight: "18px" }}>
                                        {cat.title}
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {selectedCategory && (
                        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "700", color: "#192873" }}>
                                Servicio específico
                            </label>
                            <select
                                value={selectedServiceId}
                                onChange={(e) => setSelectedServiceId(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    border: "1px solid #E1E2EC",
                                    backgroundColor: "#FFFFFF",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "14px",
                                    color: "#192873",
                                    outline: "none"
                                }}
                            >
                                <option value="" disabled>-- Seleccione un servicio --</option>
                                {getServicesForCategory(selectedCategory).map(service => (
                                    <option key={service.id} value={service.id}>
                                        {service.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedServiceId && companies && companies.length > 0 && (
                        <div style={{ marginTop: "20px", display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>
                            <label style={{ fontSize: "14px", fontWeight: "700", color: "#192873" }}>
                                Solicitar en representación de:
                            </label>
                            <select
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    border: "1px solid #E1E2EC",
                                    backgroundColor: "#FFFFFF",
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: "14px",
                                    color: "#192873",
                                    outline: "none"
                                }}
                            >
                                <option value="">A título personal</option>
                                {companies.map(comp => (
                                    <option key={comp.id} value={`${comp.id},${comp.company_name}`}>
                                        {comp.company_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "16px", alignItems: "center", marginTop: "32px" }}>
                        <MuiButton
                            onClick={closeModal}
                            sx={{
                                color: "#192873",
                                fontWeight: 700,
                                fontSize: "14px",
                                textTransform: "none",
                                borderRadius: "8px",
                                px: 2,
                                "&:hover": { backgroundColor: "#F0F3FF" }
                            }}
                        >
                            Cancelar
                        </MuiButton>
                        <MuiButton
                            disabled={!selectedServiceId}
                            onClick={handleContinue}
                            variant="contained"
                            disableElevation
                            sx={{
                                backgroundColor: selectedServiceId ? "#192873" : "#C6C5D0",
                                color: selectedServiceId ? "#FFFFFF" : "#767680",
                                fontWeight: 700,
                                fontSize: "14px",
                                textTransform: "none",
                                borderRadius: "8px",
                                px: 3,
                                boxShadow: "none",
                                "&:hover": { backgroundColor: selectedServiceId ? "#192873" : "#C6C5D0" }
                            }}
                        >
                            Continuar con la solicitud
                        </MuiButton>
                    </div>
                </DialogContent>
            </Dialog>
        </SidebarContainer>
    );
}

export default DashboardSidebar;
